<template>
  <v-dialog v-model="openModal" max-height="700" min-height="400" persistent scrollable width="1100px">
    <v-card>
      <v-card-title>
        <span class="section-title">{{ cardTitle }}</span>
        <v-spacer />
        <export-data :data-fetcher="getDataToExport" :disabled="!$hasPiiAccess" :filename="logTitle" ghost />
      </v-card-title>
      <infinite-scroll v-model="loading" :percentualUpdate="90" scrollType="box" @update-data="loadMoreLogs">
        <v-card-text v-if="logsData.length > 0">
          <v-row v-for="(log, index) in logsData" :key="index">
            <v-col class="pb-1 pt-0 info-text" cols="3">Usuário: </v-col>
            <v-col class="pb-1 pt-0 info-text info-text__bold" cols="9">{{ log.user.login }}</v-col>
            <v-col class="pb-1 pt-0 info-text" cols="3">Data e hora: </v-col>
            <v-col class="pb-1 pt-0 info-text info-text__bold" cols="9">{{ formatDate(log.created_at) }}</v-col>
            <v-col class="pb-1 pt-0 info-text" cols="3">Dados alterados: </v-col>
            <v-col class="pb-0 pt-5" cols="12">
              <h3 class="empashis-title">Alterações:</h3>
            </v-col>
            <v-col class="py-0" cols="12">
              <div v-for="(descriptions, index) in checkArray(log.delta)" :key="index" class="emphasis-box mb-5">
                <v-row>
                  <v-col class="py-0 texto-corrido" cols="12">{{ descriptions.description }}</v-col>
                  <v-col class="py-0" cols="12">Antigo: {{ checkFormat(descriptions.old) }}</v-col>
                  <v-col class="py-0" cols="12">Novo: {{ checkFormat(descriptions.new) }}</v-col>
                </v-row>
              </div>
            </v-col>
            <v-col class="py-5">
              <v-divider />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else-if="loading"></v-card-text>
        <v-card-text v-else>
          <v-row align="center" class="emphasis-box" justify="center">
            <p class="texto-corrido mb-0">Não há dados</p>
          </v-row>
        </v-card-text>
      </infinite-scroll>
      <mf-action-buttons
        class="mr-2"
        :primary-button="{
          text: 'Fechar',
          action: closeModal,
          isVisible: true,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { QUERY_LIST_LOGS } from './graphql'
import ExportData from '@/components/ExportData.vue'
import { DateTime } from 'luxon'

export default {
  name: 'LogsModal',
  components: {
    InfiniteScroll: () => import('@/components/InfiniteScroll.vue'),
    ExportData,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    tags: {
      type: String,
      required: true,
    },
    logTitle: {
      type: String,
      required: true,
    },
    idItem: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    pageNumber: 1,
    records: 0,
    logsData: [],
  }),
  computed: {
    openModal: {
      get: function () {
        return this.value
      },
      set: function (newValue) {
        this.$emit('input', newValue)
      },
    },
    cardTitle() {
      let textFormat

      if (this.logTitle === 'sms') {
        textFormat = this.logTitle.toUpperCase()
      } else {
        textFormat = this.logTitle
      }

      return `Log de alterações ${textFormat}`
    },
  },
  watch: {
    value() {
      if (this.value) this.getLogsData()
    },
  },
  methods: {
    closeModal() {
      this.logsData = []
      this.pageNumber = 1
      this.records = 0
      this.openModal = false
    },
    loadMoreLogs() {
      if (this.records === this.logsData.length) return

      this.pageNumber += 1
      this.getLogsData()
    },
    getDataToExport() {
      try {
        const rows = this.logsData.reduce((logsArray, currentLog) => {
          const alteredArray = currentLog.delta
          if (alteredArray.length) {
            alteredArray.map((item) => {
              logsArray.push([
                currentLog.user.name,
                this.formatDate(currentLog.created_at),
                item.description ?? '',
                JSON.stringify(this.checkFormat(item.old)) ?? '',
                JSON.stringify(this.checkFormat(item.new)) ?? '',
              ])
            })
          } else {
            logsArray.push([currentLog.user.name, this.formatDate(currentLog.created_at)])
          }
          return logsArray
        }, [])
        const headers = ['usuario', 'data e hora', 'alterações', 'antigo', 'novo']
        return { headers, rows }
      } catch (error) {
        console.error(error)
        this.$snackbar({ message: 'Erro ao exportar .csv', snackbarColor: 'error' })
      }
    },
    getLogsData() {
      this.loading = true
      this.$apollo
        .query({
          query: QUERY_LIST_LOGS,
          variables: {
            fieldFilters: {
              key: '_id',
              value: this.idItem,
            },
            tags: [this.tags],
            page: this.pageNumber,
            pageSize: 10,
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { operationLogs } }) => {
          this.loading = false
          this.logsData.push(...operationLogs.operationLogs)
          this.records = operationLogs.records
          this.pageNumber = operationLogs.page
        })
        .catch((error) => {
          console.error(error)
          this.loading = false
          this.$snackbar({ message: 'Erro ao buscar dados.' })
        })
    },
    formatDate(date, hasNoHour) {
      let fmtIn
      let fmtOut

      if (hasNoHour) {
        fmtIn = 'yyyy-MM-dd'
        fmtOut = 'dd/MM/yyyy'
      } else {
        fmtIn = 'yyyy-MM-dd HH:mm:ss'
        fmtOut = 'dd/MM/yyyy HH:mm'
      }

      return DateTime.fromFormat(date, fmtIn).toFormat(fmtOut)
    },
    checkFormat(item) {
      const dateRegex = new RegExp(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
      const isDate = dateRegex.test(item.value)
      const hasNoHour = item.type !== 'DATE'
      return item.type === 'DATE' || isDate ? this.formatDate(item.value, hasNoHour) : item.value
    },
    checkArray(delta) {
      return Array.isArray(delta) ? delta : []
    },
  },
}
</script>

<style lang="scss">
.info-text {
  font-size: 16px;
  color: $cinza-0;
  &__bold {
    font-weight: 700;
  }
}
.empashis-title {
  font-size: 18px;
  color: $cinza-d10;
}
.emphasis-box {
  background-color: $cinza-l90;
  border-radius: 5px;
  padding: 15px;
}
.link {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.01em;
  color: $marca-0-l40 !important;
  text-transform: none;
  -webkit-text-decoration-line: none;
  text-decoration-line: none;
}
</style>
